/* eslint-disable no-useless-escape */

const MOBILE_UA_PATTERNS = [
  /avantgo|bada\/|blackberry|blazer|compal|elaine/i,
  /fennec|hiptop|iemobile|ip(hone|od)|iris|kindle/i,
  /lge |maemo|midp|mmp|netfront/i,
  /opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\//i,
  /plucker|pocket|psp|series[46]0|symbian|treo/i,
  /up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i,
];

const MOBILE_UA_COMPOSITES = [
  { primary: /android|bb\d+|meego/i, also: /mobile/i },
  { primary: /mobile/i, also: /firefox/i },
];

const MOBILE_PREFIX_PATTERNS = [
  /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s/i,
  /a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)/i,
  /amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw/i,
  /au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)/i,
  /bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi/i,
  /ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw/i,
  /da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o/i,
  /ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8/i,
  /ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560/i,
  /gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit/i,
  /hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c/i,
  /ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)/i,
  /i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno/i,
  /ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji/i,
  /kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)/i,
  /lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w/i,
  /m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr/i,
  /me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do)/i,
  /mo(t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa/i,
  /n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)/i,
  /ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im/i,
  /op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg/i,
  /pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2/i,
  /po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qtek/i,
  /qc(07|12|21|32|60|\-[2-7]|i\-)|r380|r600|raks|rim9/i,
  /ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)/i,
  /sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)/i,
  /sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)/i,
  /sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)/i,
  /ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo/i,
  /to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)/i,
  /utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)/i,
  /vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)/i,
  /w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu/i,
  /x700|yas\-|your|zeto|zte\-/i,
];

// An iPhone in "Request Desktop Website" mode - and every iPad since iPadOS 13,
// where it is the DEFAULT - presents this token instead of `iPhone`/`iPad`. The
// resulting user agent is byte-for-byte a desktop Safari one, so no UA table can
// tell the two apart.
const APPLE_DESKTOP_UA_PATTERN = /macintosh/i;

// A real Mac reports 0: macOS has never shipped a touchscreen. WebKit keeps
// reporting the hardware's touch points in desktop mode, so this is the one
// signal the mode cannot spoof (it is also Apple's own recommended check).
const MIN_TOUCH_POINTS = 1;

const matchesAnyChunk = (input, chunks) =>
  chunks.some((re) => re.test(input));

const matchesAnyComposite = (ua, composites) =>
  composites.some(({ primary, also }) => primary.test(ua) && also.test(ua));

// Returns null (and only null) when there is no browser to inspect, so callers
// can tell "not a browser" apart from "browser with an empty user agent".
const resolveUserAgent = () => {
  if (typeof navigator === 'undefined' && typeof window === 'undefined') {
    return null;
  }

  return (
    (typeof navigator !== 'undefined' &&
      (navigator.userAgent || navigator.vendor)) ||
    (typeof window !== 'undefined' && window.opera) ||
    ''
  );
};

const readMaxTouchPoints = () => {
  if (typeof navigator === 'undefined') return 0;

  const points = navigator.maxTouchPoints;
  return typeof points === 'number' && Number.isFinite(points) ? points : 0;
};

const matchesAppleDesktopUa = (ua) =>
  APPLE_DESKTOP_UA_PATTERN.test(ua) && readMaxTouchPoints() > MIN_TOUCH_POINTS;

/**
 * True when an Apple touch device is presenting a desktop (macOS) user agent.
 *
 * Two ways to get here: the user tapped "Solicitar Site para Computador" in
 * Safari on an iPhone, or they are on an iPad, where iPadOS 13+ does it by
 * default. In both cases the UA claims macOS while the hardware still reports
 * multi-touch - which no Mac ever does.
 *
 * `isMobile` already accounts for this; the predicate is exported on its own so
 * tracking can measure how often it happens.
 */
export const isIosDesktopUa = () => {
  const ua = resolveUserAgent();
  return ua === null ? false : matchesAppleDesktopUa(ua);
};

export const isMobile = () => {
  const ua = resolveUserAgent();

  if (ua === null) return false;

  if (matchesAppleDesktopUa(ua)) return true;
  if (matchesAnyComposite(ua, MOBILE_UA_COMPOSITES)) return true;
  if (matchesAnyChunk(ua, MOBILE_UA_PATTERNS)) return true;
  return matchesAnyChunk(ua.substr(0, 4), MOBILE_PREFIX_PATTERNS);
};
